import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users,
  Plus,
  X,
  Trash2,
  Edit3,
  Save,
  Wallet,
  TrendingDown,
  TrendingUp,
  ArrowLeft,
  Receipt,
  HandCoins,
  Banknote,
  Camera,
  Upload,
  Bell,
  Loader2,
  Filter,
  Calendar,
  BadgeDollarSign,
  Printer,
  Search,
} from "lucide-react";

import { printEmployeeVoucher, printEmployeeInfo, printAdvanceLedger } from "../../utils/thermalPrint";
import { smartMatch } from "../../utils/arabicSearch";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const VOUCHER_META = {
  salary_disbursement: { label: "سند صرف راتب", color: "#E85D45", icon: Receipt, sign: "-", target: "salary" },
  advance_out:         { label: "سند دفع سلفة", color: "#F5A623", icon: HandCoins, sign: "+", target: "advance" },
  advance_in:          { label: "سند قبض سلفة", color: "#7ED321", icon: Banknote, sign: "-", target: "advance" },
};

// =============== Reusable 6-photo strip ===============
const PhotoSlot = ({ value, onChange, label, idx }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("صورة فقط"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("أقل من 5 ميغا"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd);
      onChange(data.path);
    } catch { toast.error("فشل الرفع"); }
    finally { setUploading(false); }
  };
  return (
    <label
      data-testid={`employee-photo-slot-${idx}`}
      className="flex flex-col items-center gap-1 cursor-pointer"
    >
      <div className="relative w-20 h-20 rounded-2xl border-2 border-dashed border-[#3B82F6] bg-white overflow-hidden flex items-center justify-center">
        {value ? (
          <img src={`${API}/files/${value}`} alt={label} className="w-full h-full object-cover" />
        ) : (
          <Camera className="w-5 h-5 text-[#5C6E64]" />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-white" />
          </div>
        )}
        {value && !uploading && (
          <button type="button" onClick={(e) => { e.preventDefault(); onChange(null); }}
            className="absolute top-0.5 left-0.5 w-5 h-5 bg-[#E85D45] text-white rounded-full flex items-center justify-center">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      <span className="text-[9px] text-[#5C6E64]">{label}</span>
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
    </label>
  );
};

// =============== Employee Form (create/edit) ===============
const EmployeeForm = ({ employee, onClose, onSaved }) => {
  const [form, setForm] = useState({
    full_name: employee?.full_name || "",
    job_title: employee?.job_title || "",
    employee_number: employee?.employee_number || "",
    address: employee?.address || "",
    base_salary: employee?.base_salary || "",
    base_advance: employee?.base_advance || "",
    photos: [...(employee?.photos || []), null, null, null, null, null, null].slice(0, 6),
  });
  const [saving, setSaving] = useState(false);

  const setPhoto = (i, v) => {
    const next = [...form.photos]; next[i] = v;
    setForm({ ...form, photos: next });
  };

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("اسم الموظف مطلوب"); return; }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        job_title: form.job_title.trim(),
        employee_number: form.employee_number.trim(),
        address: form.address.trim(),
        base_salary: Number(form.base_salary) || 0,
        base_advance: Number(form.base_advance) || 0,
        photos: form.photos.filter(Boolean),
      };
      if (employee) {
        await axios.patch(`${API}/admin/employees/${employee.id}`, payload);
        toast.success("تم التحديث");
      } else {
        await axios.post(`${API}/admin/employees`, payload);
        toast.success("تمت الإضافة");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" dir="rtl"
      data-testid="employee-form-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-lg text-[#1E3A5F]">
            {employee ? "تعديل الموظف" : "إضافة موظف جديد"}
          </h3>
          <button onClick={onClose} data-testid="employee-form-close">
            <X className="w-5 h-5 text-[#5C6E64]" />
          </button>
        </div>

        <div className="space-y-2">
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            data-testid="employee-full-name" placeholder="اسم الموظف *"
            className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6]" />
          <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            data-testid="employee-job-title" placeholder="مهنة الموظف"
            className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6]" />
          <input value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })}
            data-testid="employee-number" placeholder="رقم الموظف"
            className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6]" dir="ltr" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="employee-address" placeholder="عنوان السكن"
            className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6]" />
          <input type="number" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })}
            data-testid="employee-base-salary" placeholder="الراتب الأساسي (د.ع)"
            className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6]" />
          <input type="number" value={form.base_advance} onChange={(e) => setForm({ ...form, base_advance: e.target.value })}
            data-testid="employee-base-advance" placeholder="السلفة المسموحة — راتب ثانٍ (د.ع)"
            className="w-full px-3 py-2 rounded-xl border border-[#F5A623] bg-[#FFF8EC]" />

          <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-2">
            <p className="text-xs font-bold text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Upload className="w-3 h-3" /> صور الموظف (6 صور)
            </p>
            <div className="grid grid-cols-3 gap-2 justify-items-center">
              {[0,1,2,3,4,5].map((i) => (
                <PhotoSlot key={i} idx={i} label={`صورة ${i+1}`}
                  value={form.photos[i]} onChange={(v) => setPhoto(i, v)} />
              ))}
            </div>
          </div>
        </div>

        <button onClick={submit} disabled={saving} data-testid="employee-form-submit"
          className="w-full mt-4 bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 rounded-full">
          {saving ? "جاري الحفظ..." : (employee ? "حفظ التعديل" : "إضافة الموظف")}
        </button>
      </div>
    </div>
  );
};

// =============== Voucher Form ===============
const VoucherForm = ({ employees, defaultKind, defaultEmployee, onClose, onSaved }) => {
  const [employeeId, setEmployeeId] = useState(defaultEmployee?.id || "");
  const [kind, setKind] = useState(defaultKind || "salary_disbursement");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!employeeId) { toast.error("اختر الموظف"); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغ صحيح"); return; }
    setBusy(true);
    try {
      const { data: created } = await axios.post(`${API}/admin/employees/${employeeId}/vouchers`, { kind, amount: amt, note });
      toast.success(`تم تسجيل ${VOUCHER_META[kind].label}`);
      // Compute the AFTER-balance so the printed receipt shows the new
      // outstanding advance for the employee (advance_remaining = paid_out − paid_back).
      const emp = employees?.find?.((x) => x.id === employeeId) || defaultEmployee || { id: employeeId };
      const before = Number(emp.advance_remaining || 0);
      const delta = kind === "advance_out" ? amt : kind === "advance_in" ? -amt : 0;
      const empAfter = { ...emp, advance_remaining: before + delta };
      if (await confirmDialog("هل تريد طباعة السند الآن؟")) {
        printEmployeeVoucher(created || { id: Date.now().toString(), kind, amount: amt, note, created_at: new Date().toISOString() }, empAfter);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  const meta = VOUCHER_META[kind];

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" dir="rtl"
      data-testid="voucher-form-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: meta.color }}>
            <meta.icon className="w-5 h-5" />
            {meta.label}
          </h3>
          <button onClick={onClose} data-testid="voucher-form-close"><X className="w-5 h-5" /></button>
        </div>

        {!defaultEmployee && (
          <>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الموظف</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              data-testid="voucher-employee-select"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] mb-3">
              <option value="">— اختر الموظف —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} {e.job_title ? `(${e.job_title})` : ""}</option>)}
            </select>
          </>
        )}
        {defaultEmployee && (
          <div className="bg-[#F0F9FF] border border-[#3B82F6] rounded-xl px-3 py-2 mb-3 text-sm">
            الموظف: <span className="font-bold">{defaultEmployee.full_name}</span>
          </div>
        )}

        {!defaultKind && (
          <>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">نوع السند</label>
            <div className="grid grid-cols-3 gap-1 mb-3">
              {Object.entries(VOUCHER_META).map(([k, m]) => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  data-testid={`voucher-kind-${k}`}
                  className={`text-[10px] font-bold py-2 rounded-full ${kind === k ? "text-white" : "bg-[#F3F6F4] text-[#5C6E64]"}`}
                  style={kind === k ? { backgroundColor: m.color } : {}}>
                  {m.label}
                </button>
              ))}
            </div>
          </>
        )}

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ (د.ع)</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
          data-testid="voucher-amount" placeholder="0"
          className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] mb-3 text-lg font-bold" dir="ltr" />

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">ملاحظة (اختياري)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)}
          data-testid="voucher-note" placeholder="ملاحظة"
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] mb-4" />

        <button onClick={submit} disabled={busy} data-testid="voucher-submit"
          className="w-full text-white font-bold py-2 rounded-full disabled:opacity-50"
          style={{ backgroundColor: meta.color }}>
          {busy ? "جاري الحفظ..." : "حفظ السند"}
        </button>
      </div>
    </div>
  );
};

// =============== Edit Voucher Modal — fix typos without losing voucher_number ===============
const EditVoucherModal = ({ voucher, onClose, onSaved }) => {
  const meta = VOUCHER_META[voucher.kind] || {};
  const [amount, setAmount] = useState(String(voucher.amount || ""));
  const [note, setNote] = useState(voucher.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغ صحيح"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/employees/vouchers/${voucher.id}`, { amount: amt, note });
      toast.success("تم تعديل السند");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التعديل");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[115] bg-black/60 flex items-center justify-center p-3" dir="rtl"
      data-testid="voucher-edit-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#F5A623]/30">
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: meta.color }}>
            <Edit3 className="w-5 h-5" />
            تعديل {meta.label}
          </h3>
          <button onClick={onClose} data-testid="voucher-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-[#F8FAFC] rounded-xl p-3 mb-3 text-xs space-y-1 border border-[#E4EBE6]">
          <p>الموظف: <span className="font-bold">{voucher.employee_name || "—"}</span></p>
          <p>رقم السند: <span dir="ltr" className="font-bold">#{voucher.voucher_number || "—"}</span></p>
          <p>التاريخ: <span dir="ltr">{(voucher.created_at || "").slice(0, 16).replace("T", " ")}</span></p>
        </div>

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ (د.ع)</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
          data-testid="voucher-edit-amount"
          className="w-full px-3 py-2 rounded-xl border-2 border-[#F5A623] mb-3 text-lg font-bold" dir="ltr" />

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">ملاحظة (اختياري)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)}
          data-testid="voucher-edit-note"
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] mb-4" />

        <button onClick={submit} disabled={busy} data-testid="voucher-edit-submit"
          className="w-full text-white font-bold py-2 rounded-full disabled:opacity-50"
          style={{ backgroundColor: meta.color }}>
          {busy ? "جاري الحفظ..." : "حفظ التعديل"}
        </button>
      </div>
    </div>
  );
};

// =============== Employee Detail (statement) ===============
const EmployeeDetailModal = ({ employeeId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [showVoucherForm, setShowVoucherForm] = useState(null); // kind or null
  const [editingVoucher, setEditingVoucher] = useState(null);
  // Date-range filter for the voucher list (inclusive). Empty = no filter.
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const fetchData = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/employees/${employeeId}/statement`);
      setData(d);
    } catch { toast.error("فشل التحميل"); }
  };
  useEffect(() => { fetchData(); }, [employeeId]);

  const deleteVoucher = async (id) => {
    if (!(await confirmDialog("حذف هذا السند؟"))) return;
    try {
      await axios.delete(`${API}/admin/employees/vouchers/${id}`);
      toast.success("تم الحذف");
      fetchData();
      onChanged && onChanged();
    } catch { toast.error("فشل الحذف"); }
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-[105] bg-black/60 flex items-center justify-center p-3" dir="rtl">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const e = data.employee;

  return (
    <div className="fixed inset-0 z-[105] bg-black/70 flex items-center justify-center p-2" dir="rtl"
      data-testid="employee-detail-modal" onClick={onClose}>
      <div className="bg-[#F8FAFC] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-start sticky top-0 z-10">
          <div>
            <h3 className="font-bold text-xl">{e.full_name}</h3>
            <p className="text-xs opacity-90">{e.job_title || "—"} · <span dir="ltr">#{e.employee_number || "—"}</span></p>
            <p className="text-[10px] opacity-80 mt-0.5">{e.address || ""}</p>
          </div>
          <button onClick={onClose} data-testid="employee-detail-close"
            className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Photos */}
        {e.photos && e.photos.length > 0 && (
          <div className="p-3 bg-white border-b">
            <div className="grid grid-cols-6 gap-2">
              {e.photos.map((p, i) => (
                <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer"
                  className="aspect-square block rounded-xl overflow-hidden border-2 border-[#E4EBE6]">
                  <img src={`${API}/files/${p}`} alt={`photo-${i}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Salary & Advance stats */}
        <div className="p-3 space-y-3">
          {e.needs_attention_30d && (
            <div className="bg-[#F5A623]/15 border-2 border-[#F5A623] rounded-2xl p-3 flex items-center gap-2"
              data-testid="employee-30day-banner">
              <Bell className="w-5 h-5 text-[#F5A623] animate-pulse" />
              <p className="text-sm font-bold text-[#a06e10]">
                وصل <span dir="ltr">{e.days_since_start}</span> يوم عمل من بداية التسجيل!
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-3" data-testid="employee-salary-card">
              <div className="flex items-center gap-1 mb-1">
                <BadgeDollarSign className="w-4 h-4" />
                <p className="text-[10px]">الراتب المتبقي هذا الشهر</p>
              </div>
              <p className="text-2xl font-black" dir="ltr">{fmt(e.salary_remaining_this_month)}</p>
              <p className="text-[10px] opacity-80">من راتب <span dir="ltr">{fmt(e.base_salary)}</span></p>
              <p className="text-[10px] opacity-80">مصروف هذا الشهر: <span dir="ltr">{fmt(e.salary_paid_this_month)}</span></p>
            </div>
            <div className="bg-gradient-to-l from-[#F5A623] to-[#dc9320] text-white rounded-2xl p-3" data-testid="employee-advance-card">
              <div className="flex items-center gap-1 mb-1">
                <HandCoins className="w-4 h-4" />
                <p className="text-[10px]">السلفة المتبقّية (راتب ثانٍ)</p>
              </div>
              <p className="text-2xl font-black" dir="ltr">{fmt(e.advance_remaining)}</p>
              <p className="text-[10px] opacity-80">من سلفة <span dir="ltr">{fmt(e.base_advance)}</span></p>
              <p className="text-[10px] opacity-80">دفع: <span dir="ltr">{fmt(e.total_advances_out)}</span> · قبض: <span dir="ltr">{fmt(e.total_advances_in)}</span></p>
            </div>
          </div>

          {/* Quick voucher buttons */}
          <div className="grid grid-cols-3 gap-1">
            {Object.entries(VOUCHER_META).map(([k, m]) => (
              <button key={k} onClick={() => setShowVoucherForm(k)}
                data-testid={`employee-quick-${k}`}
                className="text-white text-[10px] font-bold py-2 px-1 rounded-xl flex items-center justify-center gap-1"
                style={{ backgroundColor: m.color }}>
                <m.icon className="w-3.5 h-3.5" />
                <span className="truncate">{m.label}</span>
              </button>
            ))}
          </div>

          {/* Thermal print buttons (HOIN/80mm). Each uses window.print() on a
              fresh 80mm-styled child window so the OS prints to whichever
              Bluetooth/USB printer the user picks. */}
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => printEmployeeInfo(e, {
                total_salary: e.total_salaries_out,
                total_advance_out: e.total_advances_out,
                total_advance_in: e.total_advances_in,
                advance_balance: e.advance_remaining,
              })}
              data-testid={`emp-print-info-${e.id}`}
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white text-[10px] font-bold py-2 px-2 rounded-xl flex items-center justify-center gap-1"
            >
              <Printer className="w-3.5 h-3.5" />
              طباعة بطاقة الموظف
            </button>
            <button
              type="button"
              onClick={() => printAdvanceLedger(e, data?.vouchers || [])}
              data-testid={`emp-print-ledger-${e.id}`}
              className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-[10px] font-bold py-2 px-2 rounded-xl flex items-center justify-center gap-1"
            >
              <Printer className="w-3.5 h-3.5" />
              طباعة كشف السلف
            </button>
          </div>

          {/* Vouchers list grouped by month */}
          <div className="bg-white rounded-2xl p-3 border border-[#E4EBE6]">
            <h4 className="font-bold text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Calendar className="w-4 h-4" /> كشف الحساب
            </h4>

            {/* Date-range filter — empty = show all */}
            <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-2 mb-3"
              data-testid="voucher-filter-bar">
              <p className="text-[10px] font-bold text-[#1E3A5F] mb-1 flex items-center gap-1">
                <Filter className="w-3 h-3" /> فلترة حسب التاريخ
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[#5C6E64]">من تاريخ</label>
                  <input
                    type="date" value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                    data-testid="voucher-filter-from"
                    className="w-full px-2 py-1.5 rounded-lg border border-[#BAE6FD] text-xs" />
                </div>
                <div>
                  <label className="text-[9px] text-[#5C6E64]">إلى تاريخ</label>
                  <input
                    type="date" value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                    data-testid="voucher-filter-to"
                    className="w-full px-2 py-1.5 rounded-lg border border-[#BAE6FD] text-xs" />
                </div>
              </div>
              {(filterFrom || filterTo) && (
                <button
                  type="button"
                  onClick={() => { setFilterFrom(""); setFilterTo(""); }}
                  data-testid="voucher-filter-clear"
                  className="mt-1 text-[10px] bg-white border border-[#E4EBE6] hover:bg-[#FEE2E2] hover:border-[#E85D45] text-[#E85D45] rounded-full px-2 py-0.5 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> مسح الفلتر
                </button>
              )}
            </div>
            {(() => {
              // Apply the date-range filter to the by_month grouping before rendering.
              const inRange = (iso) => {
                if (!iso) return true;
                const d = iso.slice(0, 10);
                if (filterFrom && d < filterFrom) return false;
                if (filterTo && d > filterTo) return false;
                return true;
              };
              const filteredByMonth = {};
              Object.entries(data.by_month).forEach(([mk, arr]) => {
                const kept = (arr || []).filter((v) => inRange(v.created_at));
                if (kept.length) filteredByMonth[mk] = kept;
              });
              const monthKeys = Object.keys(filteredByMonth).sort().reverse();
              if (monthKeys.length === 0) {
                return (
                  <p className="text-sm text-[#5C6E64] text-center py-4" data-testid="voucher-list-empty">
                    {(filterFrom || filterTo) ? "لا توجد سندات ضمن الفترة المحددة" : "لا توجد سندات"}
                  </p>
                );
              }
              return monthKeys.map((mk) => (
                <div key={mk} className="mb-3">
                  <p className="text-[10px] font-black text-[#1E3A5F] bg-[#E0F2FE] inline-block px-3 py-0.5 rounded-full mb-2" dir="ltr">{mk}</p>
                  <div className="space-y-1">
                    {filteredByMonth[mk].map((v) => {
                      const m = VOUCHER_META[v.kind] || {};
                      const Icon = m.icon || Receipt;
                      return (
                        <div key={v.id} data-testid={`voucher-row-${v.id}`}
                          className="flex items-center justify-between bg-[#F8FAFC] rounded-xl p-2 border border-[#E4EBE6]">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-4 h-4 shrink-0" style={{ color: m.color }} />
                            <div className="min-w-0">
                              <p className="text-xs font-bold" style={{ color: m.color }}>{m.label}</p>
                              {v.note && <p className="text-[10px] text-[#5C6E64] truncate">{v.note}</p>}
                              <p className="text-[9px] text-[#5C6E64]" dir="ltr">{(v.created_at || "").slice(0, 16).replace("T", " ")}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-sm" style={{ color: m.color }} dir="ltr">
                              {m.sign}{fmt(v.amount)}
                            </p>
                            <button onClick={() => printEmployeeVoucher(v, e)}
                              data-testid={`voucher-print-${v.id}`}
                              title="طباعة السند"
                              className="text-[#1E3A5F] hover:bg-[#1E3A5F]/10 rounded p-1">
                              <Printer className="w-3 h-3" />
                            </button>
                            <button onClick={() => setEditingVoucher(v)}
                              data-testid={`voucher-edit-${v.id}`}
                              title="تعديل السند"
                              className="text-[#F5A623] hover:bg-[#F5A623]/10 rounded p-1">
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button onClick={() => deleteVoucher(v.id)}
                              data-testid={`voucher-delete-${v.id}`}
                              className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {showVoucherForm && (
          <VoucherForm
            employees={[]}
            defaultEmployee={e}
            defaultKind={showVoucherForm}
            onClose={() => setShowVoucherForm(null)}
            onSaved={() => { fetchData(); onChanged && onChanged(); }}
          />
        )}
        {editingVoucher && (
          <EditVoucherModal
            voucher={editingVoucher}
            onClose={() => setEditingVoucher(null)}
            onSaved={() => { fetchData(); onChanged && onChanged(); }}
          />
        )}
      </div>
    </div>
  );
};

// =============== Voucher Ledger (sub-tab — shows all of one kind across employees) ===============
const VoucherLedger = ({ kind, employees, onBack, refreshKey }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  // Date-range + employee filter — sent to backend so a 3-day window only
  // pulls those rows.
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const meta = VOUCHER_META[kind];
  const Icon = meta.icon;

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { kind };
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const { data } = await axios.get(`${API}/admin/employees/vouchers`, { params });
      setList(data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, [kind, refreshKey, filterFrom, filterTo]);

  const total = useMemo(() => list.reduce((s, v) => s + (v.amount || 0), 0), [list]);

  const remove = async (id) => {
    if (!(await confirmDialog("حذف هذا السند؟"))) return;
    try {
      await axios.delete(`${API}/admin/employees/vouchers/${id}`);
      toast.success("تم الحذف");
      fetchList();
    } catch { toast.error("فشل"); }
  };

  return (
    <div className="space-y-3" data-testid={`voucher-ledger-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6] rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1"
          data-testid="voucher-ledger-back">
          <ArrowLeft className="w-3 h-3 rotate-180" /> رجوع
        </button>
        <button onClick={() => setShowForm(true)} data-testid="voucher-ledger-add"
          className="text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1"
          style={{ backgroundColor: meta.color }}>
          <Plus className="w-3 h-3" /> سند جديد
        </button>
      </div>

      <div className="text-white rounded-2xl p-4" style={{ backgroundColor: meta.color }}>
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-5 h-5" />
          <h3 className="font-bold text-lg">{meta.label}</h3>
        </div>
        <div className="flex items-baseline justify-between mt-2">
          <p className="text-xs opacity-90">إجمالي السندات{(filterFrom || filterTo) ? " (ضمن الفلتر)" : ""}</p>
          <p className="text-2xl font-black" dir="ltr">{fmt(total)} د.ع</p>
        </div>
        <p className="text-[10px] opacity-80 mt-1">عدد السندات: <span dir="ltr" className="font-bold">{list.length}</span></p>
      </div>

      {/* Date-range filter (server-side) */}
      <div className="bg-white border border-[#E4EBE6] rounded-2xl p-3" data-testid={`ledger-filter-${kind}`}>
        <p className="text-[10px] font-bold text-[#1E3A5F] mb-2 flex items-center gap-1">
          <Filter className="w-3 h-3" /> فلترة حسب فترة زمنية
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-[#5C6E64]">من تاريخ</label>
            <input
              type="date" value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              data-testid={`ledger-filter-from-${kind}`}
              className="w-full px-2 py-1.5 rounded-lg border border-[#BAE6FD] text-xs"
            />
          </div>
          <div>
            <label className="text-[9px] text-[#5C6E64]">إلى تاريخ</label>
            <input
              type="date" value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              data-testid={`ledger-filter-to-${kind}`}
              className="w-full px-2 py-1.5 rounded-lg border border-[#BAE6FD] text-xs"
            />
          </div>
        </div>
        {(filterFrom || filterTo) && (
          <button
            type="button"
            onClick={() => { setFilterFrom(""); setFilterTo(""); }}
            data-testid={`ledger-filter-clear-${kind}`}
            className="mt-2 text-[10px] bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full px-2 py-1 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> مسح الفلتر
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-8 text-center text-[#5C6E64]">
          <Icon className="w-8 h-8 mx-auto opacity-30 mb-2" />
          {(filterFrom || filterTo) ? "لا توجد سندات ضمن الفترة المحددة" : "لا توجد سندات"}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((v) => (
            <div key={v.id} data-testid={`ledger-row-${v.id}`}
              className="bg-white rounded-2xl p-3 border border-[#E4EBE6] flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-bold text-[#1E3A5F]">{v.employee_name}</p>
                {v.note && <p className="text-xs text-[#5C6E64] truncate">{v.note}</p>}
                <p className="text-[10px] text-[#5C6E64]" dir="ltr">{(v.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-sm" style={{ color: meta.color }} dir="ltr">
                  {meta.sign}{fmt(v.amount)} د.ع
                </p>
                <button onClick={() => setEditingVoucher(v)} data-testid={`ledger-edit-${v.id}`}
                  title="تعديل السند"
                  className="text-[#F5A623] hover:bg-[#F5A623]/10 rounded p-1">
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => remove(v.id)} data-testid={`ledger-delete-${v.id}`}
                  className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <VoucherForm
          employees={employees}
          defaultKind={kind}
          onClose={() => setShowForm(false)}
          onSaved={fetchList}
        />
      )}
      {editingVoucher && (
        <EditVoucherModal
          voucher={editingVoucher}
          onClose={() => setEditingVoucher(null)}
          onSaved={fetchList}
        />
      )}
    </div>
  );
};

// =============== Main EmployeesTab — sub-nav + employee list ===============
const EmployeesTab = () => {
  const [section, setSection] = useState("home"); // home | employees | salary_disbursement | advance_in | advance_out
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Search query for the employee list — matches name, job title, phone,
  // or employee number using Arabic-aware smartMatch.
  const [searchQuery, setSearchQuery] = useState("");

  // Apply Arabic-aware filter across name, job title, phone, address, and
  // employee number. Computed at the top-level so React hook order stays
  // identical regardless of which section we render below.
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    return employees.filter((e) =>
      smartMatch(
        `${e.full_name || ""} ${e.job_title || ""} ${e.phone || ""} ${e.employee_number || ""} ${e.address || ""}`,
        searchQuery
      )
    );
  }, [employees, searchQuery]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/employees`),
        axios.get(`${API}/admin/employees-summary`),
      ]);
      setEmployees(eRes.data || []);
      setSummary(sRes.data);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [refreshKey]);

  const removeEmployee = async (emp) => {
    if (!(await confirmDialog(`حذف الموظف "${emp.full_name}" وكل سنداته؟`))) return;
    try {
      await axios.delete(`${API}/admin/employees/${emp.id}`);
      toast.success("تم الحذف");
      setRefreshKey((k) => k + 1);
    } catch { toast.error("فشل"); }
  };

  if (loading && section === "home") {
    return <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>;
  }

  // ============= Home sub-nav (4 entries) =============
  if (section === "home") {
    return (
      <div className="space-y-4" data-testid="employees-home">
        {/* Top summary */}
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5" />
            <h3 className="font-bold text-lg">أرشيف الموظفين</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div data-testid="summary-total-advance-remaining">
              <p className="text-[10px] opacity-80">إجمالي السلف المتبقّية</p>
              <p className="text-2xl font-black" dir="ltr">{fmt(summary?.total_advance_remaining)} <span className="text-xs">د.ع</span></p>
            </div>
            <div data-testid="summary-employees-count">
              <p className="text-[10px] opacity-80">عدد الموظفين</p>
              <p className="text-2xl font-black" dir="ltr">{summary?.total_employees || 0}</p>
            </div>
          </div>
          {summary?.needs_attention_count > 0 && (
            <div className="mt-3 bg-[#F5A623]/30 border border-[#F5A623] rounded-xl p-2 flex items-center gap-2" data-testid="employees-30day-alert">
              <Bell className="w-4 h-4 animate-pulse" />
              <p className="text-xs font-bold">
                <span dir="ltr">{summary.needs_attention_count}</span> موظف وصل 30 يوم من بداية التسجيل
              </p>
            </div>
          )}
        </div>

        {/* Sub-nav tiles — only salary disbursement is global; loans are managed inside each employee detail */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setSection("employees")} data-testid="enter-employees"
            className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-5 hover:shadow-xl flex flex-col items-center gap-2 min-h-[110px]">
            <Users className="w-8 h-8" />
            <span className="font-bold">الموظفون</span>
            <span className="text-[10px] opacity-80" dir="ltr">{summary?.total_employees || 0}</span>
          </button>
          {Object.entries(VOUCHER_META)
            .filter(([k]) => k === "salary_disbursement")
            .map(([k, m]) => (
              <button key={k} onClick={() => setSection(k)} data-testid={`enter-${k}`}
                className="text-white rounded-2xl p-5 hover:shadow-xl flex flex-col items-center gap-2 min-h-[110px]"
                style={{ backgroundColor: m.color }}>
                <m.icon className="w-8 h-8" />
                <span className="font-bold text-sm text-center">{m.label}</span>
              </button>
            ))}
        </div>
        <p className="text-[11px] text-[#5C6E64] text-center px-2">
          ملاحظة: السلف (دفع / قبض) تُسجّل وتُعرض داخل ملف الموظف نفسه — مع صوره ومعلوماته.
        </p>
      </div>
    );
  }

  // ============= Voucher ledger sub-page =============
  if (section !== "employees") {
    return (
      <VoucherLedger
        kind={section}
        employees={employees}
        onBack={() => { setSection("home"); setRefreshKey((k) => k + 1); }}
        refreshKey={refreshKey}
      />
    );
  }

  // ============= Employees list sub-page =============
  return (
    <div className="space-y-3" data-testid="employees-list">
      <div className="flex justify-between items-center">
        <button onClick={() => setSection("home")} data-testid="employees-back"
          className="bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6] rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1">
          <ArrowLeft className="w-3 h-3 rotate-180" /> رجوع
        </button>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-employee-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1">
          <Plus className="w-3 h-3" /> إضافة موظف
        </button>
      </div>

      {/* Smart search bar — name, job title, phone, or employee number */}
      <div className="relative" data-testid="employees-search-bar">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          value={searchQuery}
          onChange={(ev) => setSearchQuery(ev.target.value)}
          placeholder="ابحث باسم الموظف أو المهنة أو رقم الهاتف..."
          data-testid="employees-search-input"
          className="w-full pr-10 pl-10 py-2.5 rounded-full border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6] text-sm bg-white"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            data-testid="employees-search-clear"
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full w-6 h-6 flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        )}
        {searchQuery && (
          <p className="text-[10px] text-[#5C6E64] mt-1 pr-2">
            عُثر على <span className="font-bold text-[#1E3A5F]" dir="ltr">{filteredEmployees.length}</span> نتيجة من <span dir="ltr">{employees.length}</span>
          </p>
        )}
      </div>

      {filteredEmployees.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">{searchQuery ? "لا توجد نتائج مطابقة" : "لا يوجد موظفون بعد"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredEmployees.map((e) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`employee-card-${e.id}`}
              onClick={() => setDetailId(e.id)}
              className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] hover:border-[#3B82F6] cursor-pointer shadow-sm relative"
            >
              {e.needs_attention_30d && (
                <Bell className="w-5 h-5 text-[#F5A623] absolute top-2 left-2 animate-pulse" />
              )}
              <div className="flex items-center gap-2 mb-2">
                {e.photos?.[0] ? (
                  <img src={`${API}/files/${e.photos[0]}`} alt={e.full_name} className="w-12 h-12 rounded-full object-cover border-2 border-[#3B82F6]" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center">
                    <Users className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#111814] truncate">{e.full_name}</p>
                  <p className="text-xs text-[#5C6E64] truncate">
                    {e.job_title || "—"} · <span dir="ltr">#{e.employee_number || "—"}</span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="bg-[#1E3A5F]/10 rounded-xl px-2 py-1.5">
                  <p className="text-[9px] text-[#1E3A5F]">راتب متبقّي</p>
                  <p className="font-black text-sm text-[#1E3A5F]" dir="ltr">{fmt(e.salary_remaining_this_month)}</p>
                </div>
                <div className="bg-[#F5A623]/15 rounded-xl px-2 py-1.5">
                  <p className="text-[9px] text-[#a06e10]">سلفة متبقّية</p>
                  <p className="font-black text-sm text-[#a06e10]" dir="ltr">{fmt(e.advance_remaining)}</p>
                </div>
              </div>

              <div className="flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                <button onClick={() => setDetailId(e.id)} data-testid={`view-employee-${e.id}`}
                  className="flex-1 bg-[#3B82F6] text-white text-[10px] font-bold py-1.5 rounded-full">عرض الكشف</button>
                <button onClick={() => { setEditing(e); setShowForm(true); }} data-testid={`edit-employee-${e.id}`}
                  className="bg-[#F5A623] text-white px-2 py-1.5 rounded-full">
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => removeEmployee(e)} data-testid={`delete-employee-${e.id}`}
                  className="bg-[#E85D45] text-white px-2 py-1.5 rounded-full">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showForm && (
        <EmployeeForm employee={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => setRefreshKey((k) => k + 1)} />
      )}

      {detailId && (
        <EmployeeDetailModal
          employeeId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
};

export default EmployeesTab;
